import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  State,
} from '@progress/kendo-data-query';
import {
  ShoppingListResponse,
  ShoppingListResponseItem,
} from 'src/types/faviriteProducts/getShoppingList';
import { FavoriteFolder, UpdateFavoriteProductRequest } from 'src/types/favorite';
import { ShoppingListProduct } from 'src/types/shopping_lists/ShoppingList';
import { normalizeNullOrEmptyFilters } from 'src/utils/normalizeNullOrEmptyFilters';
import { getProductImageSrc, parseProductImageUrl } from 'src/utils/parseProductImageUrl';

export const SHOPPING_LIST_ATTRIBUTE_TYPES = [
  'isOfflineSupplierItem',
  'isFormulary',
  'isImageAvailable',
  'isSpecialOrder',
  'hasOldProductId',
  'isContracted',
] as const;

export type ShoppingListAttributeType = (typeof SHOPPING_LIST_ATTRIBUTE_TYPES)[number];

export interface AttributeFilterConfig {
  operator: 'and' | 'or';
  attributes: string[];
}

const isCompositeFilter = (
  filter: CompositeFilterDescriptor | FilterDescriptor | null | undefined
): filter is CompositeFilterDescriptor =>
  filter != null && 'filters' in filter && Array.isArray(filter.filters);

export const extractAttributesFilter = (
  filter: CompositeFilterDescriptor | undefined
): AttributeFilterConfig | null => {
  if (!filter?.filters?.length) {
    return null;
  }

  for (const filterItem of filter.filters) {
    if (!filterItem) {
      continue;
    }

    if (isCompositeFilter(filterItem)) {
      const nested = extractAttributesFilter(filterItem);
      if (nested) {
        return nested;
      }
      continue;
    }

    if (filterItem.field !== 'attributes') {
      continue;
    }

    const value = String(filterItem.value ?? '').trim();
    if (!value) {
      return null;
    }

    return {
      operator: filterItem.operator === 'and' ? 'and' : 'or',
      attributes: value.split(',').filter(Boolean),
    };
  }

  return null;
};

export const getAttributesFilterKey = (
  filter: CompositeFilterDescriptor | undefined
): string => {
  const extracted = extractAttributesFilter(filter);
  if (!extracted?.attributes.length) {
    return '';
  }

  return `${extracted.operator}:${extracted.attributes.join(',')}`;
};

export const shouldResetItemFilterForAttributes = (
  previousKey: string,
  nextKey: string
): boolean => Boolean(nextKey) && nextKey !== previousKey;

export const shouldClearAttributesForContractedItems = (
  itemFilterValue: number,
  attributesFilterKey: string
): boolean => itemFilterValue === 1 && Boolean(attributesFilterKey);

export const EMPTY_GRID_FILTER: CompositeFilterDescriptor = { logic: 'and', filters: [] };

export const removeFieldFromFilter = (
  filter: CompositeFilterDescriptor | undefined,
  field: string
): CompositeFilterDescriptor | undefined => {
  if (!filter?.filters?.length) {
    return undefined;
  }

  const filters = filter.filters
    .map((filterItem) => {
      if (!filterItem) {
        return null;
      }

      if (isCompositeFilter(filterItem)) {
        return removeFieldFromFilter(filterItem, field) ?? null;
      }

      if (filterItem.field === field) {
        return null;
      }

      return filterItem;
    })
    .filter((filterItem): filterItem is CompositeFilterDescriptor | FilterDescriptor =>
      filterItem != null
    );

  if (filters.length === 0) {
    return undefined;
  }

  return { ...filter, filters };
};

const productHasAttribute = (product: ShoppingListProduct, attribute: string): boolean => {
  const attributeFlags: Record<string, boolean | undefined> = {
    isOfflineSupplierItem: product.isOfflineSupplierItem,
    isFormulary: product.isFormulary,
    isImageAvailable: product.isImageAvailable,
    isSpecialOrder: product.isSpecialOrder,
    hasOldProductId: product.hasOldProductId,
    isContracted: product.isContracted,
  };

  return attributeFlags[attribute] ?? product.attributes?.includes(attribute) ?? false;
};

export const applyAttributesFilter = (
  products: ShoppingListProduct[],
  config: AttributeFilterConfig | null
): ShoppingListProduct[] => {
  if (!config?.attributes.length) {
    return products;
  }

  return products.filter((product) => {
    if (config.operator === 'or') {
      return config.attributes.some((attribute) => productHasAttribute(product, attribute));
    }

    return config.attributes.every((attribute) => productHasAttribute(product, attribute));
  });
};

export const applyToolbarFilters = (
  products: ShoppingListProduct[],
  itemFilterValue: number,
  selectedCatalogFilter: number | 'all'
): ShoppingListProduct[] =>
  products.filter((product) => {
    if (itemFilterValue === 1 && !product.isContracted) {
      return false;
    }

    if (selectedCatalogFilter !== 'all' && product.catalogId !== selectedCatalogFilter) {
      return false;
    }

    return true;
  });

export const applySearchTextFilter = (
  products: ShoppingListProduct[],
  searchText: string
): ShoppingListProduct[] => {
  const query = searchText.trim().toLowerCase();
  if (!query) {
    return products;
  }

  return products.filter((product) => {
    const itemNum = product.itemNum?.toLowerCase() ?? '';
    const description = product.description?.toLowerCase() ?? '';

    return itemNum.includes(query) || description.includes(query);
  });
};

const DATE_COMPARISON_OPERATORS = new Set(['eq', 'neq', 'gt', 'gte', 'lt', 'lte']);

const excludeNullFromDateComparisonFilters = (
  filter: CompositeFilterDescriptor | undefined
): CompositeFilterDescriptor | undefined => {
  if (!filter?.filters?.length) {
    return filter;
  }

  const filters = filter.filters.map((filterItem) => {
    if (!filterItem) {
      return filterItem;
    }

    if (isCompositeFilter(filterItem)) {
      return excludeNullFromDateComparisonFilters(filterItem) ?? filterItem;
    }

    if (
      filterItem.field === 'lastPurchaseDate' &&
      DATE_COMPARISON_OPERATORS.has(String(filterItem.operator))
    ) {
      return {
        logic: 'and' as const,
        filters: [{ field: 'lastPurchaseDate', operator: 'isnotnull' }, filterItem],
      };
    }

    return filterItem;
  });

  return { ...filter, filters };
};

export const buildShoppingListProcessState = (dataState: State): State => ({
  ...dataState,
  filter:
    normalizeNullOrEmptyFilters(
      excludeNullFromDateComparisonFilters(removeFieldFromFilter(dataState.filter, 'attributes'))
    ) ?? EMPTY_GRID_FILTER,
});

const toGridDateValue = (value: string | null | undefined): Date | null => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return null;
  }

  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

export { toGridDateValue };

type GridDateSource = {
  lastPurchaseDate: null | string;
};

export const prepareGridDateFields = <T extends GridDateSource>(
  products: T[]
): (Omit<T, 'lastPurchaseDate'> & {
  lastPurchaseDate: Date | null;
  lastPurchaseDateRaw: string | null;
})[] =>
  products.map((product) => ({
    ...product,
    lastPurchaseDateRaw: product.lastPurchaseDate?.trim() || null,
    lastPurchaseDate: toGridDateValue(product.lastPurchaseDate),
  }));

export type GridReadyShoppingListProduct = Omit<ShoppingListProduct, 'lastPurchaseDate'> & {
  lastPurchaseDate: Date | null;
  lastPurchaseDateRaw: string | null;
};

export const prepareProductsForGrid = (
  products: ShoppingListProduct[]
): GridReadyShoppingListProduct[] => prepareGridDateFields(products);

export const mapGridFieldToApiField = (field: string): string => {
  const fieldMap: Record<string, string> = {
    description: 'productName',
    catalogName: 'catalog',
    companyName: 'supplierName',
    weight: 'aveWeight',
    sortOrder: 'sortPriority',
  };

  return fieldMap[field] ?? field;
};

const resolveShoppingListItemFlags = (item: ShoppingListResponseItem) => ({
  isOfflineSupplierItem: item.isOfflineSupplierItem ?? item.isOfflineSupplier ?? false,
  isFormulary: item.isFormulary ?? item.isCompanySpecified ?? false,
  isImageAvailable: item.isImageAvailable ?? Boolean(item.url),
  isSpecialOrder: item.isSpecialOrder ?? false,
  hasOldProductId: item.hasOldProductId ?? false,
  isContracted: item.isContracted ?? false,
});

export const buildShoppingListAttributes = (
  flags: ReturnType<typeof resolveShoppingListItemFlags>
): string[] =>
  [
    flags.isContracted ? 'isContracted' : null,
    flags.isOfflineSupplierItem ? 'isOfflineSupplierItem' : null,
    flags.isSpecialOrder ? 'isSpecialOrder' : null,
    flags.isImageAvailable ? 'isImageAvailable' : null,
    flags.isFormulary ? 'isFormulary' : null,
    flags.hasOldProductId ? 'hasOldProductId' : null,
  ].filter((attribute): attribute is string => attribute !== null);

export const mapShoppingListApiItem = (item: ShoppingListResponseItem): ShoppingListProduct => {
  const flags = resolveShoppingListItemFlags(item);
  const productDescription = item.productName?.trim() ?? '';
  const transformedUrl = getProductImageSrc(item.url, {
    productDescription,
    viewType: 'advanced',
  });
  const parsedUrl = parseProductImageUrl(item.url, {
    productDescription,
    viewType: 'advanced',
  });

  return {
    productKey: `${item.catalogId}-${item.categoryId}-${item.productId}`,
    productId: item.productId,
    supplierId: item.supplierId ?? item.offlineSupplierId ?? 0,
    offlineSupplierId: item.offlineSupplierId ?? 0,
    supplierCompanyId: item.supplierCompanyId ?? 0,
    itemNum: item.itemNum?.trim() ?? '',
    description: productDescription,
    casePack: item.casePack?.trim() ?? '',
    manufacturerNumber: item.manufacturerNumber?.trim() ?? '',
    price: item.price ?? 0,
    uom: item.uom?.trim().toUpperCase() ?? '',
    companyName: item.supplierName?.trim() ?? '',
    categoryId: item.categoryId,
    catalogName: item.catalog?.trim() ?? '',
    catalogId: item.catalogId,
    url: item.url ?? null,
    brandName: item.brandName?.trim() ?? '',
    weight: item.aveWeight?.trim() ?? '',
    quantity: item.quantity ?? 0,
    lastPurchaseDate: item.lastPurchaseDate?.trim() ?? '',
    isOfflineSupplierItem: flags.isOfflineSupplierItem,
    isContracted: flags.isContracted,
    isSpecialOrder: flags.isSpecialOrder,
    hasOldProductId: flags.hasOldProductId,
    isImageAvailable: flags.isImageAvailable,
    isFormulary: flags.isFormulary,
    isNoItem: item.isNoItem,
    attributes: buildShoppingListAttributes(flags),
    linkUrl: parsedUrl.linkUrl,
    altText: productDescription,
    imageSrc: transformedUrl,
    imageDescription: productDescription,
    isImage: Boolean(flags.isImageAvailable && item.url),
    hasUrl: Boolean(item.url),
    par: item.par,
    sortOrder: item.sortPriority,
    ...(parsedUrl as Partial<ShoppingListProduct>),
  };
};

export const mapShoppingListResponse = (
  data: ShoppingListResponse
): { totalRecords: number; items: ShoppingListProduct[] } => ({
  totalRecords: data.totalRecords,
  items: (data.items ?? []).map(mapShoppingListApiItem),
});

export const isShoppingListParEditable = (folder?: FavoriteFolder): boolean => {
  if (!folder) {
    return false;
  }

  // isShare: list shared to you by owner — PAR is read-only
  if (folder.isShare) {
    return false;
  }

  // isSharedToOthers, isCopy, or your own list — PAR is editable
  return true;
};

export const buildUpdateFavoriteProductRequest = (
  folderId: number,
  product: ShoppingListProduct,
  par: number
): UpdateFavoriteProductRequest => ({
  folderId,
  items: [
    {
      productId: product.productId,
      catalogId: product.catalogId,
      categoryId: product.categoryId,
      sortPriority: product.sortOrder ?? 0,
      par,
    },
  ],
});

export const buildReorderFavoriteProductRequest = (
  folderId: number,
  products: ShoppingListProduct[]
): UpdateFavoriteProductRequest => ({
  folderId,
  items: products.map((product) => ({
    productId: product.productId,
    catalogId: product.catalogId,
    categoryId: product.categoryId,
    sortPriority: product.sortOrder ?? 0,
    par: product.par == null ? null : Number(product.par),
  })),
});
